import React, { useState, useEffect } from 'react';
import GlassCard from '../../../components/ui/v2/GlassCard';
import GlassButton from '../../../components/ui/v2/GlassButton';
import { interviewService } from '../../../services/interviewService';
import { creditService } from '../../../services/creditService';
import type { InterviewDetail } from '../../../models/v2/interviews';
import {
    RiCheckLine,
    RiSparklingFill,
    RiBarChartBoxLine,
    RiArrowLeftLine,
    RiVideoLine
} from 'react-icons/ri';

/* --- Components --- */

const ScoreBadge = ({ label, score, colorClass = "text-brand-400" }: { label: string, score: number, colorClass?: string }) => (
    <div className="flex flex-col items-center">
        <div className={`text-3xl font-bold ${colorClass}`}>{Number(score || 0).toFixed(2)}</div>
        <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
    </div>
);

const MetricBar = ({ label, value, color = "bg-brand-500" }: { label: string, value: number, color?: string }) => (
    <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
            <span className="text-gray-300">{label}</span>
            <span className="text-white font-medium">{Number(value || 0).toFixed(2)}</span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div className={`h-full ${color} rounded-full`} style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
        </div>
    </div>
);

/* --- Main Screen --- */

interface InterviewResultScreenProps {
    questions: any[];
    handleFinishInterview: () => void;
    pendingUploadsCount?: number;
    interviewId?: string | null;
    isDark?: boolean;
}

export default function InterviewResultScreen({
    questions: _initialQuestions,
    handleFinishInterview,
    pendingUploadsCount = 0,
    interviewId: propInterviewId,
    isDark = true
}: InterviewResultScreenProps & { isDark?: boolean }) {
    // State
    const [viewState, setViewState] = useState<'loading' | 'initial' | 'analyzing' | 'simple' | 'full'>('loading');
    const [simpleResult, setSimpleResult] = useState<any>(null); // simple finalize response
    const [progress, setProgress] = useState(0);
    const [analysisStep, setAnalysisStep] = useState(0);

    // Data State
    const [fullResult, setFullResult] = useState<InterviewDetail | null>(null);
    const [activeQuestionId, setActiveQuestionId] = useState<string | null>(null);
    const [isLoadingResult, setIsLoadingResult] = useState(false);

    // Audio/Video logic for playback


    // Steps for the "AI Analysis" display
    const processingSteps = [
        { label: "Verifying Uploads", duration: 1500 },
        { label: "Transcribing Audio", duration: 3000 },
        { label: "Analyzing Communication", duration: 3000 },
        { label: "Generating Insights", duration: 2000 },
    ];

    // Auto-detect if interview is already completed (History view)
    useEffect(() => {
        const checkStatus = async () => {
            const id = propInterviewId || localStorage.getItem("activeInterviewId")?.replace(/^"|"$/g, '');
            if (!id || fullResult) return;

            try {
                setIsLoadingResult(true);
                const data = await interviewService.getInterview(id);

                // If already completed, go straight to full view
                if (data.interview.status === 'completed') {
                    setFullResult(data);
                    setViewState('full');
                    if (data.questions.length > 0) {
                        setActiveQuestionId(data.questions[0].id);
                    }
                } else {
                    // Only show initial screen if NOT completed
                    setViewState('initial');
                }
            } catch (err) {
                console.error("Failed to check status:", err);
            } finally {
                setIsLoadingResult(false);
            }
        };

        checkStatus();
    }, []);

    // If we already have a full result loaded externally (e.g. from history), go straight to full
    useEffect(() => {
        if (fullResult) {
            setViewState('full');
        }
    }, [fullResult]);

    const fetchFullResult = async (id: string) => {
        setIsLoadingResult(true);
        try {
            const data = await interviewService.getInterview(id);
            setFullResult(data);
            if (data.questions.length > 0) {
                setActiveQuestionId(data.questions[0].id);
            }
        } catch (err) {
            console.error("Failed to fetch full results:", err);
        } finally {
            setIsLoadingResult(false);
        }
    };

    const handleGetResults = async () => {
        const id = propInterviewId || localStorage.getItem("activeInterviewId")?.replace(/^"|"$/g, '');
        if (!id) return;

        setViewState('analyzing');
        setProgress(0);
        setAnalysisStep(0);

        // Animation Timer
        let elapsed = 0;
        const totalDuration = processingSteps.reduce((acc, step) => acc + step.duration, 0);
        const stepInterval = setInterval(() => {
            elapsed += 100;
            const pct = Math.min(98, (elapsed / totalDuration) * 100);
            setProgress(pct);

            let stepTime = 0;
            for (let i = 0; i < processingSteps.length; i++) {
                stepTime += processingSteps[i].duration;
                if (elapsed < stepTime) {
                    setAnalysisStep(i);
                    break;
                }
            }
        }, 100);

        try {
            console.log("Finalizing interview with ID:", id);

            // 1. Call Finalize API
            const result = await interviewService.finalizeInterview({ interview_id: id });
            creditService.invalidate();
            setSimpleResult(result);

            // 2. Clear interval and show 100%
            clearInterval(stepInterval);
            setProgress(100);
            setAnalysisStep(processingSteps.length - 1);

            // 3. Show Simple Result Screen
            setTimeout(() => {
                setViewState('simple');
            }, 800);

        } catch (err) {
            console.error("❌ Finalization error:", err);
            clearInterval(stepInterval);
            setViewState('initial'); // Go back to allowing retry
            alert("Analysis failed. Please try again.");
        }
    };

    const handleViewFullDetails = async () => {
        const id = propInterviewId || localStorage.getItem("activeInterviewId")?.replace(/^"|"$/g, '');
        if (!id) return;

        await fetchFullResult(id);
        setViewState('full');
    };

    // Derived Data
    const activeQuestion = fullResult?.questions.find(q => q.id === activeQuestionId);
    const activeAnswer = fullResult?.answers.find(a => a.question_id === activeQuestionId);



    // --- RENDER VIEWS ---

    // 0. Loading Screen
    if (viewState === 'loading') {
        return (
            <div className="w-full h-full flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-500"></div>
            </div>
        );
    }

    // 1. Initial / Completed Screen
    if (viewState === 'initial') {
        const isUploading = pendingUploadsCount > 0;

        return (
            <div className="w-full h-full flex items-center justify-center min-h-[60vh]">
                <div className="text-center animate-fade-in max-w-xl mx-auto p-8">
                    <div className="mb-8 relative inline-block">
                        <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6 ${isDark ? 'bg-white/10' : 'bg-gray-100'}`}>
                            {isUploading ? (
                                <RiVideoLine className={`text-4xl animate-pulse ${isDark ? 'text-white' : 'text-gray-600'}`} />
                            ) : (
                                <RiCheckLine className="text-5xl text-emerald-500" />
                            )}
                        </div>
                        {isUploading && (
                            <div className="absolute -bottom-2 -right-2 bg-brand-500 text-white text-xs font-bold px-2 py-1 rounded-full border border-black shadow-lg">
                                {pendingUploadsCount} Pending
                            </div>
                        )}
                    </div>

                    <h2 className={`text-3xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {isUploading ? "Uploading Responses..." : "Interview Completed!"}
                    </h2>

                    <p className={`text-lg mb-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {isUploading
                            ? "Please wait while we secure your video responses. This may take a moment."
                            : "Excellent work! Your responses have been recorded successfully. Ready to see how you did?"
                        }
                    </p>

                    <div className="flex justify-center gap-4">
                        <GlassButton
                            size="lg"
                            disabled={isUploading}
                            onClick={handleGetResults}
                            className={isUploading ? 'opacity-50 cursor-not-allowed' : ''}
                        >
                            {isUploading ? `Uploading (${pendingUploadsCount})` : "Get My Results"}
                        </GlassButton>
                    </div>
                </div>
            </div>
        );
    }


    // 2. Analyzing Screen
    if (viewState === 'analyzing') {
        return (
            <div className="w-full h-full flex items-center justify-center min-h-[60vh]">
                <div className="w-full max-w-xl text-center animate-fade-in">
                    <div className="mb-8 relative">
                        <div className="w-24 h-24 bg-brand-500/10 text-brand-400 rounded-full flex items-center justify-center mx-auto mb-6 relative">
                            <div className="absolute inset-0 border-4 border-brand-500/30 rounded-full border-t-brand-500 animate-spin"></div>
                            <RiSparklingFill className="text-4xl animate-pulse" />
                        </div>
                        <h2 className={`text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Analyzing Interview
                        </h2>
                        <p className={`${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Our AI is processing your responses...
                        </p>
                    </div>

                    <div className={`rounded-2xl p-8 backdrop-blur-md max-w-md mx-auto border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white/60 border-gray-200'}`}>
                        <div className="space-y-5">
                            {processingSteps.map((step, idx) => (
                                <div key={idx} className={`flex items-center gap-4 transition-all duration-300 ${idx <= analysisStep ? 'opacity-100 transform translate-x-0' : 'opacity-40'}`}>
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center border transition-colors ${idx < analysisStep ? 'bg-brand-500 border-brand-500 text-white' :
                                        idx === analysisStep ? 'border-brand-500 text-brand-500' :
                                            'border-gray-700 text-gray-700'
                                        }`}>
                                        {idx < analysisStep ? <RiCheckLine className="text-xs" /> :
                                            idx === analysisStep ? <div className="w-2 h-2 bg-brand-500 rounded-full animate-pulse" /> :
                                                <div className={`w-2 h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-300'}`} />
                                        }
                                    </div>
                                    <span className={`text-base font-medium ${idx <= analysisStep ? (isDark ? 'text-white' : 'text-gray-900') : 'text-gray-500'}`}>{step.label}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 pt-6 border-t border-white/10">
                            <div className="flex justify-between text-xs text-gray-400 mb-2">
                                <span>Progress</span>
                                <span>{Math.round(progress)}%</span>
                            </div>
                            <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-brand-600 to-brand-400 transition-all duration-300 ease-out" style={{ width: `${progress}%` }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 3. Simple Result Screen (Interim)
    if (viewState === 'simple' && simpleResult) {
        const scores = simpleResult.scores || {};
        return (
            <div className="w-full max-w-4xl mx-auto py-12 animate-fade-in px-4">
                <div className="text-center mb-12">
                    <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Analysis Complete</h1>
                    <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Here is your interview performance summary</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                    {/* Overall Score Card */}
                    <GlassCard className={`p-8 flex flex-col items-center justify-center ${isDark ? '' : '!bg-white !border-gray-200 shadow-xl'}`}>
                        <div className="relative w-40 h-40 flex items-center justify-center mb-6">
                            {/* Placeholder Circle - can replace with CircularProgress component if available */}
                            <div className="absolute inset-0 rounded-full border-8 border-brand-500/20"></div>
                            <div className="absolute inset-0 rounded-full border-8 border-brand-500 border-t-transparent animate-spin-slow" style={{ animationDuration: '3s' }}></div>
                            <div className="flex flex-col items-center">
                                <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{Number(scores.overall || 0).toFixed(2)}</span>
                                <span className="text-sm text-gray-500 uppercase tracking-wide">Overall</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-8 w-full">
                            <ScoreBadge label="Technical" score={scores.technical || 0} colorClass="text-blue-500" />
                            <ScoreBadge label="Comm." score={scores.communication_clarity || 0} colorClass="text-emerald-500" />
                            <ScoreBadge label="Confidence" score={scores.confidence || 0} colorClass="text-amber-500" />
                        </div>
                    </GlassCard>

                    {/* Summary Card */}
                    <GlassCard className={`p-8 ${isDark ? '' : '!bg-white !border-gray-200 shadow-xl'}`}>
                        <h3 className={`text-xl font-bold mb-4 flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            <RiSparklingFill className="text-brand-500" /> Executive Summary
                        </h3>
                        <div className={`prose prose-sm max-w-none ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                            <p>{simpleResult.summary || simpleResult.ai_summary || "No summary available."}</p>
                        </div>
                    </GlassCard>
                </div>

                <div className="flex flex-col items-center gap-4">
                    <GlassButton size="lg" onClick={handleViewFullDetails} icon={<RiBarChartBoxLine />}>
                        View Full Detailed Analysis
                    </GlassButton>
                    <button onClick={handleFinishInterview} className="text-sm text-gray-500 hover:text-gray-400 underline">
                        Return to Dashboard
                    </button>
                </div>
            </div>
        );
    }

    // 4. Full Result Screen (Existing Logic) - Only if fullResult is loaded
    if (!fullResult && viewState === 'full') {
        // Fallback loading
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] animate-pulse">
                Loading Details...
            </div>
        );
    }

    if (!fullResult) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                <p className="text-red-400 mb-4">Could not load results.</p>
                <GlassButton onClick={() => window.location.reload()}>Retry</GlassButton>
            </div>
        );
    }

    return (
        <div className="w-full max-w-7xl mx-auto pt-8 pb-12 animate-fade-in space-y-8">

            {/* 1. Header & Back */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <GlassButton size="sm" onClick={handleFinishInterview} variant="secondary" icon={<RiArrowLeftLine />}>
                        Back to Dashboard
                    </GlassButton>
                    <div>
                        <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{fullResult.interview.job_title}</h1>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{fullResult.interview.company} • {new Date(fullResult.interview.created_at).toLocaleDateString()}</p>
                    </div>
                </div>

                <div className="flex items-center gap-4 bg-brand-500/10 px-6 py-2 rounded-full border border-brand-500/20">
                    <span className="text-brand-500 font-medium">Overall Score</span>
                    <span className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{Number(fullResult.interview.scores?.overall || 0).toFixed(2)}<span className="text-lg text-brand-400/60">/100</span></span>
                </div>
            </div>

            {/* 2. Top Section: Summary & Details Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                {/* Left (2/3): Interview Summary */}
                <div className="lg:col-span-2">
                    <GlassCard className={`h-full p-8 ${isDark ? '' : '!bg-white/60 !border-gray-200'}`}>
                        <div className="flex items-center gap-3 mb-8">
                            <RiBarChartBoxLine className="text-2xl text-brand-400" />
                            <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Interview Summary</h2>
                        </div>

                        <div className="grid grid-cols-3 gap-4 mb-8">
                            <ScoreBadge label="Communication" score={fullResult.interview.scores?.communication_clarity || 0} colorClass="text-emerald-500" />
                            <ScoreBadge label="Confidence" score={fullResult.interview.scores?.confidence || 0} colorClass="text-amber-500" />
                            <ScoreBadge label="Technical" score={fullResult.interview.scores?.technical || 0} colorClass="text-blue-500" />
                        </div>

                        <div className="bg-brand-500/5 border border-brand-500/20 rounded-xl p-6">
                            <h3 className="text-brand-500 font-semibold mb-3 flex items-center gap-2">
                                <RiSparklingFill /> AI Overall Assessment
                            </h3>
                            <p className={`leading-relaxed text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {fullResult.interview.ai_summary || "Our AI is generating a detailed summary of your interview performance. Please check back shortly for the full text assessment."}
                            </p>
                        </div>
                    </GlassCard>
                </div>

                {/* Right (1/3): Interview Details */}
                <div className="lg:col-span-1">
                    <GlassCard className={`h-full p-6 ${isDark ? '' : '!bg-white/60 !border-gray-200'}`}>
                        <h3 className={`text-lg font-bold mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>Interview Details</h3>
                        <div className="space-y-4">
                            <div className={`flex justify-between py-2 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Type</span>
                                <span className={`text-right ${isDark ? 'text-white' : 'text-gray-900'}`}>Technical</span>
                            </div>
                            <div className={`flex justify-between py-2 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Style</span>
                                <span className={isDark ? 'text-white' : 'text-gray-900'}>Video</span>
                            </div>
                            <div className={`flex justify-between py-2 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Questions</span>
                                <span className={isDark ? 'text-white' : 'text-gray-900'}>{fullResult.questions.length}</span>
                            </div>
                            <div className={`flex justify-between py-2 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Duration</span>
                                <span className={isDark ? 'text-white' : 'text-gray-900'}>10:00</span>
                            </div>
                            <div className="flex justify-between pt-2">
                                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Status</span>
                                <span className="text-emerald-500 font-medium capitalize">{fullResult.interview.status.replace('_', ' ')}</span>
                            </div>
                        </div>
                    </GlassCard>
                </div>
            </div>

            {/* 3. Questions Section */}
            <div className="space-y-6">
                <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Questions ({fullResult.questions.length})</h2>

                {/* Horizontal Tabs */}
                <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
                    {fullResult.questions.map((q, idx) => {
                        const ans = fullResult.answers.find(a => a.question_id === q.id);
                        const score = ans?.score || 0;
                        const isActive = activeQuestionId === q.id;

                        return (
                            <button
                                key={q.id}
                                onClick={() => setActiveQuestionId(q.id)}
                                className={`flex items-center gap-3 px-4 py-2 rounded-lg border transition-all whitespace-nowrap ${isActive
                                    ? 'bg-brand-500 text-white border-brand-400 shadow-lg shadow-brand-500/20'
                                    : isDark
                                        ? 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:border-white/20'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    }`}
                            >
                                <span className="font-bold">Q{idx + 1}: {q.category || 'General'}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${isActive ? 'bg-white/20 text-white' :
                                    score >= 70 ? 'bg-emerald-500/10 text-emerald-400' :
                                        score >= 50 ? 'bg-amber-500/10 text-amber-400' :
                                            'bg-red-500/10 text-red-400'
                                    }`}>
                                    {Number(score).toFixed(2)}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Question Detail View */}
                {activeQuestion && activeAnswer ? (
                    <div className={`border rounded-2xl p-8 shadow-2xl ${isDark ? 'bg-black border-white/10' : 'bg-white border-gray-200'}`}>
                        {/* Question Text */}
                        <div className="mb-8">
                            <div className="flex items-center gap-2 mb-2">
                                <span className="text-brand-500 font-bold text-lg">Question {fullResult.questions.findIndex(q => q.id === activeQuestionId) + 1}</span>
                                <span className={`px-2 py-0.5 rounded text-xs border uppercase tracking-wider ${isDark ? 'bg-white/10 text-gray-400 border-white/5' : 'bg-gray-100 text-gray-600 border-gray-200'}`}>{activeQuestion.category}</span>
                            </div>
                            <h3 className={`text-xl md:text-2xl font-medium leading-relaxed ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {activeQuestion.question}
                            </h3>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                            {/* Left Column: Video & Response */}
                            <div className="space-y-6">
                                {/* Video Player */}
                                <div className="aspect-video bg-black rounded-xl overflow-hidden border border-white/10 shadow-2xl relative group">
                                    {activeAnswer.video_url ? (
                                        <video
                                            src={activeAnswer.video_url}
                                            className="w-full h-full object-cover"
                                            controls
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center text-gray-500 flex-col gap-2">
                                            <RiVideoLine className="text-4xl opacity-50" />
                                            <span className="text-sm">Video unavailable</span>
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>
                                    Duration: {Math.floor((activeAnswer.duration_seconds || 0) / 60)}:{(activeAnswer.duration_seconds || 0) % 60 < 10 ? '0' : ''}{(activeAnswer.duration_seconds || 0) % 60}
                                </div>

                                {/* Transcript / Your Response */}
                                <div className="space-y-2">
                                    <h4 className={`text-sm font-bold uppercase tracking-wider ${isDark ? 'text-white' : 'text-gray-900'}`}>Your Response</h4>
                                    <div className={`p-4 rounded-xl border text-sm leading-relaxed max-h-[300px] overflow-y-auto custom-scrollbar ${isDark ? 'bg-white/5 border-white/10 text-gray-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                                        {activeAnswer.transcript ? (
                                            <p>{activeAnswer.transcript}</p>
                                        ) : (
                                            <p className="italic text-gray-500">Transcript generating...</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Right Column: Scores & Feedback */}
                            <div className="space-y-8">

                                {/* Score Breakdown */}
                                <div>
                                    <div className="flex items-baseline justify-between mb-6">
                                        <h4 className="text-lg font-bold text-white">Score Breakdown</h4>
                                        <div className="text-3xl font-bold text-brand-400">
                                            {Number(activeAnswer.score || 0).toFixed(2)}<span className="text-lg text-gray-500">/100</span>
                                        </div>
                                    </div>

                                    <div className="space-y-1">
                                        {activeAnswer.scores ? (
                                            Object.entries(activeAnswer.scores).map(([key, value]) => {
                                                if (key === 'overall') return null;
                                                return <MetricBar key={key} label={key.replace(/_/g, ' ')} value={value || 0} color="bg-brand-400" />;
                                            })
                                        ) : (
                                            <>
                                                <MetricBar label="Communication" value={activeAnswer.score_communication_clarity || 0} color="bg-emerald-500" />
                                                <MetricBar label="Confidence" value={activeAnswer.score_confidence || 0} color="bg-amber-500" />
                                                <MetricBar label="Technical" value={activeAnswer.score_technical || 0} color="bg-blue-500" />
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Technical Metrics Cards */}
                                <div>
                                    <h4 className={`text-sm font-bold uppercase tracking-wider mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Technical Metrics</h4>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                                            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {activeAnswer.delivery?.speech_rate_wpm ? Math.round(activeAnswer.delivery.speech_rate_wpm) : '-'}
                                            </div>
                                            <div className="text-xs text-gray-500 uppercase">WPM</div>
                                        </div>
                                        <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                                            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {activeAnswer.delivery?.filler_words_count ?? '-'}
                                            </div>
                                            <div className="text-xs text-gray-500 uppercase">Filler Words</div>
                                        </div>
                                        <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                                            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {activeAnswer.delivery?.volume_variance ? activeAnswer.delivery.volume_variance.toFixed(1) : '-'}
                                            </div>
                                            <div className="text-xs text-gray-500 uppercase">Vol. Variance</div>
                                        </div>
                                        <div className={`border rounded-xl p-4 text-center ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-200'}`}>
                                            <div className={`text-2xl font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {activeAnswer.delivery?.pause_density ? activeAnswer.delivery.pause_density.toFixed(1) : '-'}
                                            </div>
                                            <div className="text-xs text-gray-500 uppercase">Pause Density</div>
                                        </div>
                                    </div>
                                </div>

                                {/* AI Feedback */}
                                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-6">
                                    <h4 className="text-emerald-500 font-semibold mb-3 flex items-center gap-2">
                                        <RiSparklingFill /> AI Feedback
                                    </h4>
                                    <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                        {activeAnswer.ai_feedback || "Analyzing feedback..."}
                                    </p>
                                </div>

                                {/* AI Better Answer */}
                                {activeAnswer.ai_response && (
                                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6">
                                        <h4 className="text-blue-400 font-semibold mb-3 flex items-center gap-2">
                                            <RiSparklingFill /> Improved Answer
                                        </h4>
                                        <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                            {activeAnswer.ai_response}
                                        </p>
                                    </div>
                                )}

                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500 bg-white/5 rounded-xl border border-white/5">
                        Select a question to view details
                    </div>
                )}
            </div>
        </div>
    );
}
